import { type ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  Avatar,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import logo from '../../assets/pwa-192x192.png';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { TypeCommon } from '../../types/common';
import { schemaValidate } from '../../validation/user-register-vendor';
import Spinner from '../../components/spinner';
import ModalConfirmEmail from '../../components/modal-confirm-email-mui';
import { useAppDispatch, useAppSelector } from '../../hooks/common';
import {
  checkUserExist,
  resent,
  verifyCognito,
  verifyEmail,
  verifyLink,
  verifyUser,
} from '../../stores/slices/common.slice';
import { MSG } from '../../constants/common.constants';
import { DEFAULT_PHONE_COUNTRY_CODE } from '../../constants/phone-country.constants';
import PhoneNumberInput from '../../components/phone-number-input';
import { formatPhoneNumberByCountryCode } from '../../utils/phone.utils';

interface FormData {
  firstName: string;
  lastName: string;
  firstName_yomi: string;
  lastName_yomi: string;
  userName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  phoneCountryCode: string;
  phoneNumber: string;
  org: string;
}

const UserRegisterVendor = (): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);

  // Common TextField styles
  const textFieldStyles = {
    '& .MuiFormHelperText-root': {
      margin: '4px 0 0 0',
      fontSize: '12px',
      color: '#6b7280',
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: '12px',
      opacity: 0.7,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fbfdff',
      transition: 'box-shadow 120ms ease, border-color 120ms ease',
      '& fieldset': {
        borderColor: '#e5ebf3',
      },
      '&:hover fieldset': {
        borderColor: '#cdd8e6',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#0b5ed7',
        boxShadow: '0 0 0 3px rgba(11, 94, 215, 0.12)',
      },
    },
  };

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schemaValidate),
    defaultValues: {
      firstName: '',
      lastName: '',
      firstName_yomi: '',
      lastName_yomi: '',
      userName: '',
      email: '',
      password: '',
      passwordConfirm: '',
      phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
      phoneNumber: '',
      org: '',
    },
    mode: 'all',
  });
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(state => state.common.loading);
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const [data, setData] = useState<TypeCommon>(null);
  const [isUsernameExist, setIsUsernameExist] = useState<boolean>(false);
  const [isEmailExist, setIsEmailExist] = useState<boolean>(false);
  const [isOpenConfirmEmailModal, setIsOpenConfirmEmailModal] =
    useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] =
    useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleCancel = (): void => {
    reset({
      org: `${data?.organization_name ?? ''}`,
    });
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'info'
  ): void => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = (): void => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const togglePasswordConfirmVisibility = (): void => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const handleVerifyLink = async (): Promise<void> => {
    try {
      if (!key) {
        return;
      }
      const res: TypeCommon = await dispatch(
        verifyLink({
          key,
        })
      );
      if (res?.error) {
        showSnackbar(
          typeof res?.payload?.message === 'string'
            ? res?.payload?.message
            : MSG.ERROR_COMMON,
          'error'
        );
        return;
      }
      setData(res?.payload?.data);
      setValue('org', `${res?.payload?.data?.organization_name ?? ''}`);
    } catch (error: TypeCommon) {
      showSnackbar(error?.response?.data?.message || MSG.ERROR_COMMON, 'error');
    }
  };

  const handleCheckUserExist = async (
    searchString: string,
    isUsername?: boolean
  ): Promise<TypeCommon> => {
    try {
      if (searchString) {
        const res: TypeCommon = await dispatch(
          checkUserExist({
            search: searchString,
          })
        );
        if (res?.payload?.data?.data) {
          setLoading(false);
          // showSnackbar(
          //   typeof res?.payload?.message === 'string'
          //     ? res?.payload?.message
          //     : MSG.ERROR_COMMON,
          //   'error'
          // );
          if (isUsername) {
            setIsUsernameExist(true);
          } else setIsEmailExist(true);
          document?.getElementById('userName')?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          return true;
          return false;
        }
        if (isUsername) {
          setIsUsernameExist(false);
        } else setIsEmailExist(false);
        return false;
      }
      if (isUsername) {
        setIsUsernameExist(false);
      } else setIsEmailExist(false);
      return false;
    } catch (error: TypeCommon) {
      setLoading(false);
      return false;
    }
  };

  const onSubmit = async (formData: FormData): Promise<void> => {
    try {
      setLoading(true);
      const username = formData?.userName?.trim();
      const password = formData?.password?.trim();
      const email = formData?.email?.trim();
      const phone_number = formatPhoneNumberByCountryCode(
        formData?.phoneNumber?.trim() ?? '',
        formData?.phoneCountryCode
      );
      const checkExistUsername = await handleCheckUserExist(username, true);
      const checkExistEmail = await handleCheckUserExist(email);
      setLoading(false);
      if (!checkExistUsername && !checkExistEmail) {
        setLoading(true);
        const res: TypeCommon = await dispatch(
          verifyCognito({
            username,
            email,
            password,
            phone_number,
          })
        );
        if (res?.error) {
          setLoading(false);
          showSnackbar(
            typeof res?.payload?.message === 'string'
              ? res?.payload?.message
              : MSG.ERROR_COMMON,
            'error'
          );
          return;
        }
        setLoading(false);
        setIsOpenConfirmEmailModal(true);
      }
    } catch (error: TypeCommon) {
      setLoading(false);
      showSnackbar(error?.response?.data?.message || MSG.ERROR_COMMON, 'error');
    }
  };

  const onConfirmEmail = async (code: string): Promise<void> => {
    try {
      setLoading(true);
      const username = getValues('userName').trim();
      const res: TypeCommon = await dispatch(
        verifyEmail({
          username,
          confirmation_code: code,
        })
      );
      if (res?.error) {
        setLoading(false);
        showSnackbar(
          typeof res?.payload?.message === 'string'
            ? res?.payload?.message
            : MSG.ERROR_COMMON,
          'error'
        );
        return;
      }
      setLoading(false);
      setIsOpenConfirmEmailModal(false);
      await onVerify(getValues());
    } catch (error: TypeCommon) {
      setLoading(false);
      showSnackbar(error?.response?.data?.message || MSG.ERROR_COMMON, 'error');
    }
  };
  const handleResent = async (): Promise<void> => {
    try {
      setLoading(true);
      const username = getValues('userName').trim();
      const res: TypeCommon = await dispatch(
        resent({
          username,
        })
      );
      if (res?.error) {
        setLoading(false);
        showSnackbar(
          typeof res?.payload?.message === 'string'
            ? res?.payload?.message
            : MSG.ERROR_COMMON,
          'error'
        );
        return;
      }
      setLoading(false);
      showSnackbar(
        '確認コードが送信されました、メールをご確認ください。',
        'success'
      );
    } catch (error: TypeCommon) {
      setLoading(false);
      showSnackbar(error?.response?.data?.message || MSG.ERROR_COMMON, 'error');
    }
  };
  const onVerify = async (formData: FormData): Promise<void> => {
    try {
      setLoading(true);
      const res: TypeCommon = await dispatch(
        verifyUser({
          username: formData?.userName?.trim(),
          email: formData?.email?.trim(),
          phone_number: formatPhoneNumberByCountryCode(
            formData?.phoneNumber?.trim() ?? '',
            formData?.phoneCountryCode
          ),
          first_name: formData?.firstName?.trim(),
          last_name: formData?.lastName?.trim(),
          first_name_yomi: formData?.firstName_yomi?.trim(),
          last_name_yomi: formData?.lastName_yomi?.trim(),
          organization_id: data?.organization_id,
          registered_group: 'creator',
          role: data?.role,
          active: '1',
          service_id: data.service_id,
          link_id: data.id,
        })
      );
      if (res?.error) {
        setLoading(false);
        showSnackbar(
          typeof res?.payload?.message === 'string'
            ? res?.payload?.message
            : MSG.ERROR_COMMON,
          'error'
        );
        return;
      }
      setLoading(false);
      showSnackbar('ユーザー登録が完了しました。', 'success');
      setTimeout(() => {
        window.location.href = 'https://benri.summitgco.com/login';
      }, 1000);
      reset({
        org: `${data?.organization_name ?? ''}`,
      });
    } catch (error: TypeCommon) {
      setLoading(false);
      showSnackbar(error?.response?.data?.message || MSG.ERROR_COMMON, 'error');
    }
  };

  useEffect(() => {
    handleVerifyLink();
  }, []);

  return (
    <>
      {(loading || isLoading) && <Spinner />}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          elevation={10}
          sx={{
            width: '100%',
            maxWidth: 600,
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            backgroundColor: 'white',
            position: 'relative',
            border: '1px solid #eef2f7',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <Avatar
              src={logo}
              alt='Logo'
              sx={{
                width: { xs: 60, sm: 100 },
                height: { xs: 60, sm: 100 },
                // borderRadius: 2,
                // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                '& img': {
                  objectFit: 'cover',
                },
              }}
            />
          </Box>

          <Typography
            variant='h4'
            component='h1'
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#374151',
              mb: 1,
            }}
          >
            ユーザー登録
          </Typography>
          <Box
            component='form'
            autoComplete='off'
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* First Name and Last Name */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <FormControl fullWidth error={!!errors.firstName}>
                  <FormLabel
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    名字
                    <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                  </FormLabel>
                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        fullWidth
                        autoComplete='off'
                        placeholder='名字を入力してください'
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        inputProps={{ maxLength: 100 }}
                        size='small'
                        sx={textFieldStyles}
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth error={!!errors.lastName}>
                  <FormLabel
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    名前
                    <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                  </FormLabel>
                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        fullWidth
                        autoComplete='off'
                        placeholder='名前を入力してください'
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        inputProps={{ maxLength: 100 }}
                        size='small'
                        sx={textFieldStyles}
                      />
                    )}
                  />
                </FormControl>
              </Box>

              {/* First Name Yomi and Last Name Yomi */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <FormControl fullWidth error={!!errors.firstName_yomi}>
                  <FormLabel
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    名字（フリガナ）
                    <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                  </FormLabel>
                  <Controller
                    name='firstName_yomi'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        fullWidth
                        autoComplete='off'
                        placeholder='名字（フリガナ）を入力してください'
                        error={!!errors.firstName_yomi}
                        helperText={errors.firstName_yomi?.message}
                        inputProps={{ maxLength: 100 }}
                        size='small'
                        sx={textFieldStyles}
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth error={!!errors.lastName_yomi}>
                  <FormLabel
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    名前（フリガナ）
                    <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                  </FormLabel>
                  <Controller
                    name='lastName_yomi'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        fullWidth
                        autoComplete='off'
                        placeholder='名前（フリガナ）を入力してください'
                        error={!!errors.lastName_yomi}
                        helperText={errors.lastName_yomi?.message}
                        inputProps={{ maxLength: 100 }}
                        size='small'
                        sx={textFieldStyles}
                      />
                    )}
                  />
                </FormControl>
              </Box>

              {/* Username and Email */}
              <FormControl fullWidth error={!!errors.userName}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  ログイン名
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='userName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        setIsUsernameExist(false);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      fullWidth
                      placeholder='ログイン名を入力してください'
                      error={!!errors.userName || isUsernameExist}
                      helperText={
                        isUsernameExist
                          ? 'ログイン名は既に存在しています。'
                          : errors.userName?.message
                      }
                      inputProps={{ maxLength: 100 }}
                      autoComplete='off'
                      size='small'
                      sx={textFieldStyles}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth error={!!errors.email}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  メールアドレス
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        setIsEmailExist(false);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      fullWidth
                      type='email'
                      autoComplete='off'
                      placeholder='メールアドレスを入力してください'
                      error={!!errors.email || isEmailExist}
                      helperText={
                        isEmailExist
                          ? 'メールアドレスは既に存在しています。'
                          : errors.email?.message
                      }
                      inputProps={{ maxLength: 100 }}
                      size='small'
                      sx={textFieldStyles}
                    />
                  )}
                />
              </FormControl>

              {/* Password and Password Confirm */}
              <FormControl fullWidth error={!!errors.password}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  パスワード
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='password'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder='パスワードを入力してください'
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      inputProps={{ maxLength: 100 }}
                      autoComplete='new-password'
                      size='small'
                      sx={textFieldStyles}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge='end'
                              size='small'
                              sx={{ color: '#6b7280' }}
                            >
                              {showPassword ? (
                                <MdVisibilityOff size={20} />
                              ) : (
                                <MdVisibility size={20} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth error={!!errors.passwordConfirm}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  パスワード確認
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='passwordConfirm'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      fullWidth
                      type={showPasswordConfirm ? 'text' : 'password'}
                      placeholder='パスワードを再度入力してください'
                      error={!!errors.passwordConfirm}
                      helperText={errors.passwordConfirm?.message}
                      inputProps={{ maxLength: 100 }}
                      autoComplete='new-password'
                      size='small'
                      sx={textFieldStyles}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={togglePasswordConfirmVisibility}
                              edge='end'
                              size='small'
                              sx={{ color: '#6b7280' }}
                            >
                              {showPasswordConfirm ? (
                                <MdVisibilityOff size={20} />
                              ) : (
                                <MdVisibility size={20} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>

              {/* Phone Number and Role */}
              <FormControl fullWidth error={!!errors.phoneNumber}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  電話番号
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='phoneCountryCode'
                  control={control}
                  render={({ field: countryField }) => (
                    <Controller
                      name='phoneNumber'
                      control={control}
                      render={({ field: phoneField }) => (
                        <PhoneNumberInput
                          countryCode={countryField.value}
                          phoneNumber={phoneField.value}
                          onCountryCodeChange={countryField.onChange}
                          onPhoneNumberChange={phoneField.onChange}
                          onPhoneNumberBlur={phoneField.onBlur}
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber?.message ?? ''}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>

              {/* Organization */}
              <FormControl fullWidth error={!!errors.org}>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  組織
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <Controller
                  name='org'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      fullWidth
                      autoComplete='off'
                      placeholder='組織を選択してください'
                      error={!!errors.org}
                      helperText={errors.org?.message}
                      inputProps={{ readOnly: true }}
                      size='small'
                      sx={textFieldStyles}
                    />
                  )}
                />
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                type='submit'
                variant='contained'
                fullWidth
                sx={{
                  backgroundColor: '#0b5ed7',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '14px',
                  py: 0.75,
                  // borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 10px rgba(11, 94, 215, 0.15)',
                  '&:hover': {
                    backgroundColor: '#0a4bb5',
                    boxShadow: '0 6px 14px rgba(11, 94, 215, 0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                  },
                }}
              >
                登録
              </Button>
              <Button
                variant='outlined'
                onClick={handleCancel}
                disabled={loading}
                fullWidth
                sx={{
                  borderColor: '#0b5ed7',
                  color: '#0b5ed7',
                  fontWeight: 600,
                  fontSize: '14px',
                  py: 0.75,
                  // borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#0a4bb5',
                    backgroundColor: 'rgba(11, 94, 215, 0.06)',
                  },
                }}
              >
                クリア
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Modal Confirm Email */}
      <ModalConfirmEmail
        open={isOpenConfirmEmailModal}
        onClose={() => {
          setIsOpenConfirmEmailModal(false);
          reset();
        }}
        onConfirm={onConfirmEmail}
        onResend={handleResent}
        loading={loading}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserRegisterVendor;
